import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, Separator } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';

import { AllAccountsAssignedRow } from './AllAccountsAssignedRow';
import { ExternalAccountRow } from './ExternalAccountRow';
import { HomeExternalAccountsDocument } from './graphql/query.homeExternalAccounts.generated';
import { NoAccountsRow } from './NoAccountsRow';

import { trackClick } from '../../features/track';
import { urlBuilder } from '../../Shared/urlBuilder';

export const ExternalAccountsCard: React.FC = () => {
  const { data } = useGraphQLQuery(HomeExternalAccountsDocument, { throwOnError: true });

  if (!data) return <CardLoader />;

  if (!data.envelopes.length) return null;

  const accounts = data.externalAccounts;
  const assignedAccounts = accounts.filter(account => account.goal !== null);
  const unassignedAccounts = accounts.filter(account => account.goal === null);
  const allAccountsAssigned = accounts.length && accounts.length === assignedAccounts.length;
  const hasNoAccounts = accounts.length === 0;

  return (
    // Remove the `overflow: 'hidden'` from here once it is built into Samba card component
    <Card css={{ overflow: 'hidden' }}>
      <Card.Header css={{ display: 'flex', justifyContent: 'space-between', padding: 'space.300' }}>
        <H2 variant="title200">Other connected accounts</H2>
        {!!accounts.length && (
          <SambaAutoLink
            aria-label="Manage external accounts"
            to={urlBuilder.appExternalAccounts.absolute()}
            onClick={() => trackClick({ name: 'ManageExternalAccounts', location: 'Summary' })}
          >
            Manage
          </SambaAutoLink>
        )}
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content>
        {unassignedAccounts.length > 0 &&
          unassignedAccounts.map(account => (
            <React.Fragment key={account.id}>
              <ExternalAccountRow account={account} />
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
            </React.Fragment>
          ))}
        {!!allAccountsAssigned && (
          <>
            <AllAccountsAssignedRow />
            <Separator css={{ fullWidthWithInset: 'space.300' }} />
          </>
        )}
        {!!hasNoAccounts && <NoAccountsRow />}
      </Card.Content>
      {!hasNoAccounts && (
        <Card.Footer css={{ padding: 'space.300' }}>
          <SambaAutoLink
            to={urlBuilder.appExternalAccountSetup.absolute()}
            onClick={() => trackClick({ name: 'AddExternalAccount', location: 'Summary' })}
          >
            Connect new account
          </SambaAutoLink>
        </Card.Footer>
      )}
    </Card>
  );
};
